import { useCallback, useEffect, useRef, useState } from 'react';
import { registerFlusher, useSaves } from '../state/saves';
import type { SaveStatus } from './useAutosave';

/**
 * 값 하나(원문 전사·메모 등)를 입력 후 잠시 뒤 저장. 화면 이탈·탭 숨김 시 즉시 저장.
 * 실패하면 입력값을 유지하고 'error' 상태로 남는다 (다시 시도 가능).
 */
export function useDebouncedSave<T>(save: (v: T) => Promise<void>, delayMs = 1000) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<{ v: T } | null>(null);
  const timer = useRef<number | null>(null);
  const saveRef = useRef(save);
  const counted = useRef(false);
  const chain = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const count = (on: boolean) => {
    const s = useSaves.getState();
    if (on && !counted.current) {
      counted.current = true;
      s.inc();
    } else if (!on && counted.current) {
      counted.current = false;
      s.dec();
    }
  };

  const flush = useCallback(() => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    chain.current = chain.current.then(async () => {
      const p = pending.current;
      if (!p) return;
      pending.current = null;
      setStatus('saving');
      try {
        await saveRef.current(p.v);
        setStatus(pending.current ? 'pending' : 'saved');
        setError(null);
        if (!pending.current) count(false);
      } catch (e) {
        pending.current ??= p;
        setStatus('error');
        setError((e as Error).message);
      }
    });
    return chain.current;
  }, []);

  const schedule = useCallback(
    (v: T) => {
      pending.current = { v };
      count(true);
      setStatus('pending');
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), delayMs);
    },
    [delayMs, flush],
  );

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    const onPageHide = () => void flush();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    const unregister = registerFlusher(async () => {
      await flush();
    });
    return () => {
      unregister();
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      void flush();
    };
  }, [flush]);

  return { schedule, flush, status, error };
}
